import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data';

export default function About() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');

  const content = {
    da: {
      metaTitle: 'Om os | Billund Varme & Sanitet A/S – Din lokale VVS\'er siden 1963',
      metaDesc:
        'Billund Varme & Sanitet A/S er en veletableret VVS-virksomhed med mange års erfaring og stærke lokale rødder i Billund. Vi udfører VVS, blik- og smedearbejde.',
      canonical: 'https://billundvvs.netlify.app/om-os/',
      h1: 'Om os',
      h2: 'Din lokale VVS\'er siden 1963',
      heroLead:
        'Billund Varme & Sanitet A/S er en veletableret VVS-virksomhed med mange års erfaring og stærke lokale rødder i Billund. Vi udfører VVS, blik- og smedearbejde for både private, virksomheder og offentlige kunder – altid med fokus på kvalitet, god service og holdbare løsninger.',
      heroCta: 'Kontakt os i dag',
      heroCtaLink: '/kontakt',
      benefits: [
        {
          icon: 'fa-solid fa-users',
          title: 'Lang erfaring',
          desc: 'Vi har udført VVS-arbejde siden 1963 og har stor erfaring i branchen.',
        },
        {
          icon: 'fa-solid fa-gem',
          title: 'Høj kvalitet',
          desc: 'Vi går aldrig på kompromis med kvaliteten af vores arbejde og materialer.',
        },
        {
          icon: 'fa-solid fa-location-dot',
          title: 'Lokalt forankret',
          desc: 'Vi er en lokal virksomhed i Billund og kender området og kundernes behov.',
        },
        {
          icon: 'fa-solid fa-handshake',
          title: 'Personlig service',
          desc: 'Vi lægger vægt på en tæt dialog og en service, du kan stole på.',
        },
      ],
      versatileTitle: 'En alsidig VVS-virksomhed',
      versatileLead:
        'Vi udfører alle former for VVS, blik- og smedearbejde – fra mindre opgaver til store projekter.',
      versatileCards: [
        {
          image: '/images/plumber-working.webp',
          alt: 'Erfaring med VVS-installationer',
          isCustomAsset: false,
          title: 'Erfaring',
          desc: 'Mere end 60 års erfaring med VVS-installationer.',
        },
        {
          image: '/images/vvs-pipes-technical.webp',
          alt: 'Kvalitet i VVS-arbejde og rørinstallationer',
          isCustomAsset: false,
          title: 'Kvalitet',
          desc: 'Vi leverer solide og gennemtestede løsninger.',
        },
        {
          image:
            'https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/solrig-industriga-rd-med-ra-de-porte-og-varevogne.png',
          alt: 'Lokal service i Billund og omegn',
          isCustomAsset: true,
          cropClass: 'object-center',
          title: 'Lokal service',
          desc: 'Vi rykker hurtigt ud i Billund og omegn.',
        },
      ],
      valuesTitle: 'Vores værdier',
      valuesLead: 'Disse værdier driver vores arbejde hver dag.',
      values: [
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Pålidelighed',
          desc: 'Vi overholder aftaler og leverer til tiden.',
        },
        {
          icon: 'fa-solid fa-gear',
          title: 'Faglighed',
          desc: 'Vi holder os opdateret med ny viden og teknologi.',
        },
        {
          icon: 'fa-solid fa-users',
          title: 'Samarbejde',
          desc: 'Vi arbejder tæt sammen med kunder, rådgivere og andre håndværkere.',
        },
        {
          icon: 'fa-solid fa-leaf',
          title: 'Bæredygtighed',
          desc: 'Vi rådgiver om energieffektive og miljøvenlige løsninger.',
        },
      ],
      ctaTitle: 'Kontakt os for en uforpligtende snak',
      ctaLead:
        'Har du en VVS-opgave – stor som lille – er du altid velkommen til at kontakte os. Vi giver gerne et godt råd og et uforpligtende tilbud.',
      ctaBtnPhone: 'Ring 75 33 12 76',
      ctaBtnContact: 'Send en besked',
    },
    en: {
      metaTitle: 'About us | Billund Varme & Sanitet A/S – Local plumber since 1963',
      metaDesc:
        'Billund Varme & Sanitet A/S is an established plumbing company with decades of experience and strong roots in Billund.',
      canonical: 'https://billundvvs.netlify.app/en/about/',
      h1: 'About us',
      h2: 'Your local plumber since 1963',
      heroLead:
        'Billund Varme & Sanitet A/S is an established plumbing and heating company with many years of experience and strong local roots in Billund. We carry out plumbing, sheet metal and smith work for private, corporate and public clients – always focusing on quality, good service and long-lasting solutions.',
      heroCta: 'Contact us today',
      heroCtaLink: '/en/contact',
      benefits: [
        {
          icon: 'fa-solid fa-users',
          title: 'Decades of experience',
          desc: 'Providing plumbing services since 1963 with deep industry expertise.',
        },
        {
          icon: 'fa-solid fa-gem',
          title: 'High quality',
          desc: 'We never compromise on the quality of our craftsmanship or materials.',
        },
        {
          icon: 'fa-solid fa-location-dot',
          title: 'Locally rooted',
          desc: 'Based in Billund with in-depth knowledge of our local clients’ needs.',
        },
        {
          icon: 'fa-solid fa-handshake',
          title: 'Personal service',
          desc: 'We value close dialogue and reliable service you can depend on.',
        },
      ],
      versatileTitle: 'A versatile plumbing & heating company',
      versatileLead:
        'We undertake all types of plumbing, sheet metal and heating work – from minor repairs to large-scale contracts.',
      versatileCards: [
        {
          image: '/images/plumber-working.webp',
          alt: 'Plumbing expertise',
          isCustomAsset: false,
          title: 'Experience',
          desc: 'More than 60 years of expertise in plumbing installations.',
        },
        {
          image: '/images/vvs-pipes-technical.webp',
          alt: 'Quality craftsmanship',
          isCustomAsset: false,
          title: 'Quality',
          desc: 'We deliver solid, tested, and high-standard solutions.',
        },
        {
          image:
            'https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/solrig-industriga-rd-med-ra-de-porte-og-varevogne.png',
          alt: 'Local plumbing service in Billund',
          isCustomAsset: true,
          cropClass: 'object-center',
          title: 'Local service',
          desc: 'Prompt response across Billund and surrounding areas.',
        },
      ],
      valuesTitle: 'Our core values',
      valuesLead: 'These principles guide our work every single day.',
      values: [
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Reliability',
          desc: 'We honor agreements, keep promises, and deliver on time.',
        },
        {
          icon: 'fa-solid fa-gear',
          title: 'Professionalism',
          desc: 'We stay continuously updated with the latest knowledge and tech.',
        },
        {
          icon: 'fa-solid fa-users',
          title: 'Collaboration',
          desc: 'We work closely with customers, engineers, and fellow craftsmen.',
        },
        {
          icon: 'fa-solid fa-leaf',
          title: 'Sustainability',
          desc: 'We advise on energy-efficient and eco-friendly heating solutions.',
        },
      ],
      ctaTitle: 'Get in touch for a non-binding talk',
      ctaLead:
        'Whether your plumbing task is large or small, you are always welcome to contact us. We are happy to give expert advice and a free quotation.',
      ctaBtnPhone: 'Call +45 75 33 12 76',
      ctaBtnContact: 'Send a message',
    },
  };

  const current = isEn ? content.en : content.da;

  return (
    <div className="flex flex-col bg-white text-slate-800">
      <Helmet>
        <title>{current.metaTitle}</title>
        <meta name="description" content={current.metaDesc} />
        <link rel="canonical" href={current.canonical} />
        <meta property="og:title" content={current.metaTitle} />
        <meta property="og:description" content={current.metaDesc} />
      </Helmet>

      {/* 1. HERO SECTION - Full cover right image with seamless left gradient flow */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200/80">
        {/* Right side cover image container (Desktop: covers right half from edge to edge) */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[52%] 2xl:w-[55%] h-full overflow-hidden pointer-events-none">
          {/* Gentle edge gradient transition from white text container into image */}
          <div className="absolute inset-y-0 left-0 w-44 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/20 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent z-10 pointer-events-none" />

          <img
            src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/vvs-monta-r-ved-firmabilen-1.png"
            alt="Mogens Brøgger ved firmabil foran Billund Varme & Sanitet A/S"
            className="w-full h-full object-cover object-[50%_25%]"
            loading="eager"
          />
        </div>

        {/* Content container */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-6 xl:col-span-6 flex flex-col justify-center">
              <h1 className="text-[#06243d] font-black text-4xl sm:text-5xl lg:text-[54px] leading-[1.08] tracking-tight mb-2">
                {current.h1}
              </h1>

              <p className="text-[#06243d] font-bold text-xl sm:text-2xl mb-4 sm:mb-5">
                {current.h2}
              </p>

              <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 max-w-xl">
                {current.heroLead}
              </p>

              <div>
                <Link
                  to={current.heroCtaLink}
                  className="inline-flex items-center gap-2.5 bg-[#df0011] hover:bg-[#b7000d] text-white font-bold text-sm sm:text-base px-6 sm:px-7 py-3 sm:py-3.5 rounded-md transition-all shadow-sm hover:shadow"
                >
                  <span>{current.heroCta}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>

              {/* Mobile/Tablet Image with soft gradient flow */}
              <div className="mt-8 lg:hidden relative w-full aspect-[16/10] sm:aspect-[16/9] rounded-xl overflow-hidden shadow-sm border border-slate-200/60 bg-white">
                <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white via-white via-white/70 to-transparent z-10 pointer-events-none" />
                <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white via-white/40 to-transparent z-10 pointer-events-none" />
                <img
                  src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/vvs-monta-r-ved-firmabilen-1.png"
                  alt="Mogens Brøgger ved firmabil foran Billund Varme & Sanitet A/S"
                  className="w-full h-full object-cover object-[50%_25%]"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. FOUR FEATURES / BENEFITS ROW */}
      <section className="bg-white border-b border-slate-200/80 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {current.benefits.map((b, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-2 sm:p-0"
              >
                <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-[#06243d] text-white flex items-center justify-center shrink-0 shadow-xs">
                  <i className={`${b.icon} text-lg sm:text-xl`} aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#06243d] font-bold text-sm sm:text-base leading-snug mb-1">
                    {b.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-[13px] leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. EN ALSIDIG VVS-VIRKSOMHED */}
      <section className="bg-[#eaf3f9] py-14 sm:py-20 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 sm:mb-10">
            <h2 className="text-[#06243d] font-black text-2xl sm:text-3xl lg:text-[34px] tracking-tight mb-2">
              {current.versatileTitle}
            </h2>
            <p className="text-slate-600 text-sm sm:text-base max-w-3xl">
              {current.versatileLead}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {current.versatileCards.map((card, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg p-3 sm:p-4 shadow-xs border border-slate-200/80 flex flex-col"
              >
                <div className="relative aspect-[4/3] rounded-md overflow-hidden bg-slate-100">
                  <img
                    src={card.image}
                    alt={card.alt}
                    className={`w-full h-full object-cover ${
                      card.cropClass || ''
                    }`}
                    loading="lazy"
                  />
                </div>
                <div className="pt-4 pb-2 text-center flex-1 flex flex-col justify-center">
                  <h3 className="text-[#06243d] font-bold text-base sm:text-lg mb-1">
                    {card.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. VORES VÆRDIER */}
      <section className="bg-white py-14 sm:py-20 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 sm:mb-10">
            <h2 className="text-[#06243d] font-black text-2xl sm:text-3xl lg:text-[34px] tracking-tight mb-2">
              {current.valuesTitle}
            </h2>
            <p className="text-slate-600 text-sm sm:text-base max-w-2xl">
              {current.valuesLead}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {current.values.map((v, idx) => (
              <div
                key={idx}
                className="bg-[#f8fafc] border border-slate-200/90 rounded-lg p-5 sm:p-6 flex flex-col shadow-2xs hover:border-slate-300 transition-colors"
              >
                <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-[#06243d] text-white flex items-center justify-center shrink-0 mb-4 shadow-xs">
                  <i className={`${v.icon} text-lg sm:text-xl`} aria-hidden="true" />
                </div>
                <h3 className="text-[#06243d] font-bold text-base sm:text-lg mb-1.5">
                  {v.title}
                </h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. KONTAKT OS FOR EN UFORPLIGTENDE SNAK (BANNER) */}
      <section className="relative overflow-hidden bg-[#06243d] py-14 sm:py-20">
        {/* Background photo of premises: venstre side er 100% solid/dækkende mørkeblå, og højre side viser billedet klart */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Billedet er placeret i højre side, 100% skarpt og synligt */}
          <div className="absolute inset-y-0 right-0 w-full sm:w-[68%] lg:w-[65%] xl:w-[62%] h-full">
            <img
              src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/solrig-industriga-rd-med-ra-de-porte-og-varevogne.png"
              alt="Billund Varme & Sanitet A/S domicil med firmabiler og varmepumpe"
              className="w-full h-full object-cover object-[78%_center] sm:object-right"
              loading="lazy"
            />
          </div>

          {/* Ultra-smooth gradient overgang:
              Solid mørkeblå over teksten til venstre, og fader helt ud så opacity i højre side er 0 */}
          <div className="hidden sm:block absolute inset-0 bg-gradient-to-r from-[#06243d] from-28% via-[#06243d] via-33% via-[#06243d]/85 via-40% via-[#06243d]/35 via-50% via-[#06243d]/5 via-60% to-transparent" />

          {/* Mobil fallback mørk gradient så teksten altid er 100% letlæselig på små skærme */}
          <div className="sm:hidden absolute inset-0 bg-gradient-to-r from-[#06243d] via-[#06243d]/95 via-70% to-[#06243d]/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl sm:max-w-lg lg:max-w-xl">
            <h2 className="text-white font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">
              {current.ctaTitle}
            </h2>
            <p className="text-slate-200 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              {current.ctaLead}
            </p>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href={`tel:+45${companyInfo.phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-2.5 bg-[#df0011] hover:bg-[#b7000d] text-white font-bold text-sm sm:text-base px-6 sm:px-7 py-3.5 rounded-md transition-all shadow-md hover:shadow-lg"
              >
                <span>{current.ctaBtnPhone}</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </a>

              <Link
                to={isEn ? '/en/contact' : '/kontakt'}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/25 font-semibold text-sm sm:text-base px-5 sm:px-6 py-3.5 rounded-md transition-colors"
              >
                <span>{current.ctaBtnContact}</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
